import React, { useState } from 'react';
import SchoolSelector from '@ajat/design-system/SchoolSelector';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard } from '../../shared';

const SCHOOL_INFO = {
  statusLabel: 'DIN SKOLES WEBSHOP ER ÅBEN',
  dateRange: '4.10.2020 - 22.10.2020',
  description: 'Glem ikke at købe din studenterhue i denne periode for at få rabat.',
};

// The real popup always gets a banner from the brand wrapper — without one
// the desktop layout takes its own `:not(:has(.ds-school-selector__banner))`
// branch and looks nothing like production.
const BANNER = require('../../../../../assets/cls-dk/common/school-selector-banner.jpg');

const SCHOOLS = [
  {
    id: '1',
    slug: 'norre-gymnasium',
    name: 'Nørre Gymnasium',
    classes: ['3.A', '3.B'],
    seasons: [
      { id: 's1', slug: 'studieturstrojer', name: 'Studieturstrøjer', classes: ['3.A', '3.B'] },
      { id: 's2', slug: 'vogntrojer', name: 'Vogntrøjer', classes: ['3.A'] },
    ],
  },
  {
    id: '2',
    slug: 'roskilde-katedralskole',
    name: 'Roskilde Katedralskole',
    classes: ['2.C', '2.D'],
    seasons: [
      { id: 's3', slug: 'studieturstrojer', name: 'Studieturstrøjer', classes: ['2.C', '2.D'] },
    ],
  },
];

/** The copy CLS-DK actually ships, so the preview reads like production. */
const DK_COPY = {
  title: 'Vælg din skole',
  infoText: 'Vælg din skole for at få adgang til unikke priser, produkter og levering til skolen.',
  schoolPlaceholder: 'Skriv din skole her',
  seasonPlaceholder: 'Vælg kollektion',
  classPlaceholder: 'Vælg din klasse',
  submitLabel: 'Fortsæt',
  notFoundTitle: 'Kan du ikke finde din skole?',
  clearLabel: 'Fjern skolevalg',
};

const Stage = ({ mobile = false, children }) => (
  <div
    className={`${mobile ? 'ds-school-selector-mobile-preview' : 'ds-school-selector-preview flex items-center justify-center'} bg-[var(--color-gray-025)] p-[var(--space-ui-large-s)] rounded-[var(--radius-card)] border border-[var(--color-gray-1)]`}
  >
    {children}
  </div>
);

export const meta = {
  component: SchoolSelector,
  title: 'School Selector',
  group: 'Overlays',
  importPath: '@ajat/design-system/SchoolSelector',
  figmaNode: '3639:14235',
  description:
    'The school → shop → class picker that scopes the whole storefront. A centred popup on desktop, a bottom sheet on mobile. A single shop auto-selects and its dropdown is hidden; closing fires onClose only — there is deliberately no "skip" path.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires on X, Escape, overlay click and swipe; on submit it fires ~500ms after onSelect'],
    ['onSelect', 'func', '—', 'Fires with { school, season, class }'],
    ['schools', 'array', '[]', 'School list — filter to schools with an open shop before passing it in'],
    ['requireClass', 'bool', 'true', 'Make the class step mandatory'],
    ['requireSeason', 'bool', 'false', 'Make the shop step mandatory'],
    ['dismissible', 'bool', 'true', 'false removes the X and ignores overlay / Escape / swipe — the CLS-DK mandatory identification'],
    ['noClassesText', 'string', 'Danish default', 'Shown when the chosen shop has no classes'],
    ['preferredSeasonResolver', 'func', 'null', 'Auto-picks a shop for a school — used by header menu intents'],
    ['showPreferredSeason', 'bool', 'false', 'Keep the shop dropdown visible even when preferredSeasonResolver already picked one'],
    ['title', 'string', "'Vælg din skole'", 'Popup heading'],
    ['infoText', 'string', 'Danish default', 'Intro copy'],
    ['schoolPlaceholder', 'string', '—', 'School field label'],
    ['seasonPlaceholder', 'string', '—', 'Shop field label'],
    ['classPlaceholder', 'string', '—', 'Class field label'],
    ['submitLabel', 'string', "'Continue'", 'Submit button label'],
    ['notFoundTitle', 'string', '—', '"Can\'t find your school?" heading'],
    ['notFoundLinkLabel', 'string', '—', 'Not-found link label'],
    ['notFoundLinkHref', 'string', "'#'", 'Not-found link target'],
    ['notInListOption', 'object', 'null', '{ label } — appended to the dropdown, shown only when the search matches nothing'],
    ['notInListTitle', 'string', "''", 'Heading shown after picking the not-in-list option'],
    ['notInListCtaLabel', 'string', "''", 'CTA label in that state'],
    ['onNotInListCtaClick', 'func', 'null', 'CTA handler — fires after the popup closes'],
    ['schoolInfo', 'object', 'null', '{ statusLabel, dateRange, description } shown once a school is picked'],
    ['getSchoolInfo', 'func', 'null', 'Derives schoolInfo from the selected school'],
    ['onClear', 'func', 'null', 'Fires when the selection is cleared'],
    ['clearLabel', 'string', '—', 'Clear button label'],
    ['initialSchool', 'object', 'null', 'Preselected school — restored on every open'],
    ['initialSeason', 'object', 'null', 'Preselected shop'],
    ['initialClass', 'string', 'null', 'Preselected class'],
    ['banner', 'node', 'null', 'Banner image / node at the top'],
    ['preview', 'bool', 'false', 'Static mode — no overlay, no portal, no scroll lock'],
    ['lockScroll', 'bool', 'true', 'Lock body scroll while open'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'Brand pages never mount this directly — each Clothing/{Brand}/Common/SchoolSelector wrapper supplies the school list, the persistence and the 24h TTL.',
  usage: [
    {
      title: 'Opened by the global event',
      code: `// Anywhere in the app
window.dispatchEvent(new CustomEvent('open-school-selector', {
  detail: { menuItemKey: 'menu_1', fallbackTo: '/clothing/cls-dk/shop/studieturstrojer' },
}));

// The brand Common/Header listens and renders the wrapper,
// which reads localStorage, filters to schools with an open
// shop, and writes the selection back through
// stampSchoolSelection + notifySchoolSelectionUpdated.`,
    },
    {
      title: 'Mandatory identification (CLS-DK)',
      code: `<SchoolSelector
  isOpen={open}
  dismissible={false}        // no X, ignores Escape / overlay / swipe
  requireClass
  schools={schoolsWithOpenShop}
  onSelect={({ school, season, class: cls }) => bindCartScope(school, season, cls)}
/>`,
    },
  ],
};

export default function SchoolSelectorPage() {
  const [open, setOpen] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live" hint="as CLS-DK ships it — school, shop, then class">
        <Button size="primary" variant="main" onClick={() => setOpen(true)}>Open School Selector</Button>
        <SchoolSelector
          isOpen={open}
          onClose={() => setOpen(false)}
          onSelect={() => setOpen(false)}
          banner={BANNER}
          schools={SCHOOLS}
          schoolInfo={SCHOOL_INFO}
          requireSeason
          requireClass
          notInListOption={{ label: 'Min skole er ikke på listen' }}
          notInListTitle="Er din skole ikke på listen?"
          notInListCtaLabel="Book onlinemøde"
          onNotInListCtaClick={() => {}}
          {...DK_COPY}
        />
      </DemoCard>

      <DemoCard title="Desktop" hint="banner + school, shop and class — the production layout" surface={false}>
        <Stage>
          <SchoolSelector
            isOpen
            preview
            onClose={() => {}}
            banner={BANNER}
            schools={SCHOOLS}
            schoolInfo={SCHOOL_INFO}
            requireSeason
            {...DK_COPY}
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — school only" hint="requireClass={false}" surface={false}>
        <Stage>
          <SchoolSelector
            isOpen
            preview
            onClose={() => {}}
            banner={BANNER}
            schools={SCHOOLS}
            requireClass={false}
            schoolInfo={SCHOOL_INFO}
            {...DK_COPY}
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — without a banner" hint="the layout collapses to a single column" surface={false}>
        <Stage>
          <SchoolSelector
            isOpen
            preview
            onClose={() => {}}
            schools={SCHOOLS}
            schoolInfo={SCHOOL_INFO}
            {...DK_COPY}
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Mobile — bottom sheet" hint="the banner is hidden below 1024px" surface={false}>
        <Stage mobile>
          <SchoolSelector
            isOpen
            preview
            onClose={() => {}}
            banner={BANNER}
            schools={SCHOOLS}
            schoolInfo={SCHOOL_INFO}
            {...DK_COPY}
          />
        </Stage>
      </DemoCard>
    </ComponentPage>
  );
}
